/**
 * Routes signUp metadata to the correct Cognito destination.
 *
 * Cognito's app client only permits writing a fixed set of user attributes
 * (email, given_name, family_name, ...). Sending any other key in
 * `userAttributes` fails with "A client attempted to write unauthorized
 * attribute". Profile fields live on the Foundation account, not the Cognito
 * user, so we forward them via `ClientMetadata` — which bypasses the writable-
 * attribute ACL, is never stored on the Cognito user, and reaches the backend
 * PreSignUp trigger, which persists them to the account.
 *
 * PAIRED WITH the backend allow-list in
 * core/source/modules/accounts/helpers/auth/signupProfileFields.ts — keep the
 * two field lists in sync. The backend ignores any ClientMetadata key not on
 * its allow-list, so an extra key here is dropped rather than persisted.
 */
export const SIGNUP_PROFILE_FIELDS = [
  'name',
  'nickname',
  'picture',
  'locale',
  'timezone'
] as const

const PROFILE_FIELD_SET = new Set<string>(SIGNUP_PROFILE_FIELDS)

/**
 * Partition signUp metadata:
 * - allow-listed profile fields -> `clientMetadata` (persisted to the account)
 * - everything else (e.g. given_name, family_name) -> `userAttributes`
 *
 * Null/undefined values are dropped; all values are coerced to strings, since
 * both Cognito maps require string values.
 */
export function splitSignupMetadata(metadata: Record<string, unknown> = {}): {
  userAttributes: Record<string, string>
  clientMetadata: Record<string, string>
} {
  const userAttributes: Record<string, string> = {}
  const clientMetadata: Record<string, string> = {}

  for (const [key, value] of Object.entries(metadata)) {
    if (value === null || value === undefined) continue
    const stringValue = String(value)
    if (PROFILE_FIELD_SET.has(key)) {
      clientMetadata[key] = stringValue
    } else {
      userAttributes[key] = stringValue
    }
  }

  return { userAttributes, clientMetadata }
}
